import detectEthereumProvider from '@metamask/detect-provider'
import cookies from 'browser-cookies'
import { useSnackbar } from 'notistack'
import { FC, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useAccount, useConnect } from 'wagmi'
import { useAuth } from '../../hooks/useAuth'
import { segmentWalletProvider } from '../../lib/segment'
import { styled } from '../../lib/stitches'
import { Connectors, WalletProvider, WalletProviderType } from '../../lib/wagmi/connectors'
import MagicConnector from '../../lib/wagmi/MagicConnector'
import Button from '../core/Button/Button'
import CenteredLoadingAnimation from '../core/CenteredLoadingAnimation'
import { Flex } from '../core/Flex'
import { ArpeggiLightLogo } from '../core/icons'
import Spacer from '../core/Spacer/Spacer'
import Text from '../core/Text/Text'
import { gray15 } from '../platform/color'
import { CoinbaseWalletLogo, MetaMaskLogo, WalletConnectLogo } from '../platform/icons'
import { AuthFlowPosition } from './AuthFlowModal'
import { emailRegex } from './CreateAccount'

const InputContainer = styled('div', {
  backgroundColor: '$button__primary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '$6',
  padding: '8px 10px',
  color: gray15,
  width: '100%',
  fontWeight: '$regular',
  fontFamily: '$dm',
  fontSize: '16px'
})

const Input = styled('input', {
  background: 'none',
  border: 'none',
  outline: 'none',
  width: '100%'
})

const useConnectWallet = (
  setFlowPosition: React.Dispatch<React.SetStateAction<AuthFlowPosition>>,
  setEmailOverload?: React.Dispatch<React.SetStateAction<string>>,
  onSuccess?: () => void
) => {
  const [email, setEmail] = useState<string>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const { connectAsync } = useConnect()
  const { connector: activeConnector } = useAccount()
  const { getAndSetUser, setWalletProviderCookie } = useAuth()
  const {
    isConnecting: isActivating,
    isDisconnected,
    isConnected,
    address: walletAddress
  } = useAccount()
  const { enqueueSnackbar } = useSnackbar()

  const onClickConnectWallet = async (walletType: WalletProviderType) => {
    setLoading(true)
    try {
      const providerDetected = await detectEthereumProvider()
      segmentWalletProvider(walletType)
      if (!providerDetected && !isMobile && walletType !== WalletProvider.MAGICLINK) {
        enqueueSnackbar(
          'No ethereum wallet detected, please install one of the supported wallets and try again',
          {
            variant: 'error'
          }
        )

        return
      }

      const connector = Connectors[walletType]

      if (walletType === WalletProvider.MAGICLINK && email) {
        const magicConnector = connector as MagicConnector
        magicConnector.setEmail(email)
      }

      if (
        !(isConnected && activeConnector === connector) ||
        walletType === WalletProvider.MAGICLINK
      ) {
        try {
          await connectAsync({ connector })
        } catch (err) {
          console.error(err)
        }
      }

      setWalletProviderCookie(walletType)
      const user = await getAndSetUser()

      if (user && (user.twitterHandle === undefined || user.twitterHandle === null)) {
        setFlowPosition('verifyOnTwitter')
        return
      }
      if (user) {
        if (onSuccess) onSuccess()
        setFlowPosition('close')
        return
      }
      // User doesn't exist, so they need to create an account
      // email overloading is used when a user signs up with MagicLink
      if (email !== null && setEmailOverload && walletType === WalletProvider.MAGICLINK) {
        setEmailOverload(email)
      }
      setFlowPosition('createAccount')
      return
    } catch (error) {
      // TODO: handle wallet errors gracefully with user facing info
      console.error(error)
      enqueueSnackbar('An error occurred while connecting your wallet, please try again', {
        variant: 'modalError'
      })
      cookies.erase('current_wallet')
      cookies.erase('walletProvider')
    } finally {
      setLoading(false)
    }
  }

  return { onClickConnectWallet, loading, isActivating, setEmail, email }
}

interface Props {
  setFlowPosition: React.Dispatch<React.SetStateAction<AuthFlowPosition>>
  setEmailOverload?: React.Dispatch<React.SetStateAction<string>>
  onSuccess?: () => void
}

const ConnectWallet: FC<Props> = ({ setFlowPosition, setEmailOverload, onSuccess }) => {
  const { isUserLoading } = useAuth()
  const { loading, isActivating, onClickConnectWallet, setEmail, email } = useConnectWallet(
    setFlowPosition,
    setEmailOverload,
    onSuccess
  )

  return (
    <Flex direction={'column'} css={{ justifyContent: 'center', alignItems: 'center' }}>
      <ArpeggiLightLogo width={'42px'} height={'42px'} />

      <Spacer type={'vertical_5'} />

      <Text css={{ textAlign: 'center' }} size={'32'} type={'primary'}>
        Log in to Arpeggi
      </Text>

      <Spacer type={'vertical_2'} />

      <Text css={{ textAlign: 'center' }} size={'14'} type={'secondary'}>
        Connect your wallet to mint your songs as NFTs - free!
      </Text>

      <Spacer type={'vertical_2'} />
      {loading || isUserLoading ? (
        <CenteredLoadingAnimation />
      ) : (
        <>
          <Flex direction={'column'} width="full">
            <Button
              css={{ justifyContent: 'flex-start' }}
              type={'primary'}
              rounded={true}
              big={true}
              disabled={isActivating}
              onClick={() =>
                onClickConnectWallet(
                  isMobile && typeof window?.ethereum === 'undefined'
                    ? WalletProvider.WALLETCONNECT
                    : WalletProvider.METAMASK
                )
              }
              style={{ paddingTop: '10px', paddingBottom: '10px' }}
            >
              <MetaMaskLogo height={'22px'} width={'22px'} style={{ marginRight: '8px' }} />
              <Text size={'16'}>MetaMask</Text>
            </Button>
            <Spacer type={'vertical_1'} />
            <Button
              css={{ justifyContent: 'flex-start' }}
              type={'primary'}
              rounded={true}
              big={true}
              disabled={isActivating}
              onClick={() => onClickConnectWallet(WalletProvider.COINBASE)}
              style={{ paddingTop: '10px', paddingBottom: '10px' }}
            >
              <CoinbaseWalletLogo width={'22px'} height={'22px'} style={{ marginRight: '8px' }} />
              <Text size={'16'}>Coinbase Wallet</Text>
            </Button>
            <Spacer type={'vertical_1'} />
            <Button
              css={{ justifyContent: 'flex-start' }}
              type={'primary'}
              rounded={true}
              big={true}
              disabled={isActivating}
              onClick={() => onClickConnectWallet(WalletProvider.WALLETCONNECT)}
              style={{ paddingTop: '10px', paddingBottom: '10px' }}
            >
              <WalletConnectLogo height={'22px'} width={'22px'} style={{ marginRight: '8px' }} />{' '}
              <Text size={'16'}>WalletConnect</Text>
            </Button>
          </Flex>

          <Spacer type={'vertical_2'} />

          <Text css={{ textAlign: 'center' }} size={'14'} type={'secondary'}>
            Don't have a wallet?
            <br />
            Log in with your email & we'll create one for you.
          </Text>

          <Spacer type={'vertical_2'} />

          <Flex direction={'row'} width="full">
            <InputContainer>
              {' '}
              <Input
                placeholder={'Your email'}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isActivating}
              />
              <Button
                onClick={() => onClickConnectWallet(WalletProvider.MAGICLINK)}
                type={'dark'}
                css={{ fontSize: '14px', padding: '5px 15px' }}
                disabled={isActivating || !emailRegex.test(email)}
              >
                Log In
              </Button>
            </InputContainer>
          </Flex>
        </>
      )}
    </Flex>
  )
}

export default ConnectWallet
